import classNames from 'classnames'
import { FormikErrors, FormikProps, getIn, withFormik } from 'formik'
import { t } from 'i18next'
import React, { PropsWithChildren, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { Form } from '@lib/formik'

import { useAccountReadContext } from '@context/AccountContextContext'

import { Body1, Caption1, Heading6 } from '@ui/Typography'
import { ActionIcon } from '@ui/components/ActionIcon'
import { Button } from '@ui/components/Button'
import { Checkbox } from '@ui/components/Checkbox'
import { DocumentFileUpload } from '@ui/components/FileUpload/DocumentFileUpload'
import { DocumentFileExtensionType, FileData } from '@ui/components/FileUpload/types'
import { Textarea } from '@ui/components/Input/Textarea/Textarea'
import { Link } from '@ui/components/Link'
import { Radio } from '@ui/components/Radio'
import { InfoIcon } from '@ui/icons/Info.icon'

import {
  useCategorySizesReadContext,
  useCategorySizesWriteContext,
} from '@utils/CategorySizesContext'
import { isTickmillEU } from '@utils/companyName.utils'
import { isZero } from '@utils/validations'

import { TestAnswerDto, TestAnswersDto } from '@model/ClientTestAnswerDto'
import { ClientTestDto } from '@model/ClientTestDto'
import { DocumentCategoryType } from '@model/DocumentCategories'
import { FreeTextAnswer } from '@model/TestSectionsDto'

import { Box } from '@shared/components/Box'

import { ClassificationText } from './ClassficiationText'

import styles from './ProfessionalClassificationTestForm.module.scss'

enum RadioCodeEnum {
  SELECTED = '1',
}

interface Props {
  title: string
  text: string
  hasIcon?: boolean
  handelInfoOnClick?: () => void
}

const OverviewCard: React.FC<PropsWithChildren<Props>> = ({
  title,
  text,
  hasIcon = true,
  handelInfoOnClick,
  children,
}) => {
  return (
    <div className={classNames(styles.overview)}>
      <Box flex align='flex-start'>
        <div>
          <Body1 isParagraph medium>
            {title}
          </Body1>
        </div>
        {hasIcon && <ActionIcon icon={InfoIcon} size='x20' onClick={handelInfoOnClick} />}
      </Box>
      <Body1 isParagraph>{text}</Body1>
      {children}
    </div>
  )
}

export interface ProfessionalClassificationTestValues {
  significantTradeSize: string
  significantPortfolio: string
  professionalExperience: string

  employerName: string

  jobTitle: string

  jobDuration: string

  professionalExperienceDetail: string

  tradeSizeProofs: FileData[]
  portfolioProofs: FileData[]

  classificationDeclaration1: boolean
  classificationDeclaration2: boolean
  classificationDeclaration3: boolean

  freeAnswers: FreeTextAnswer[]
  selectedAnswers: { question: string; answer: string }[]
}

interface OuterProps {
  handleTradeSizeModalOpen: () => void
  handlePortfolioModalOpen: () => void

  testData: ClientTestDto

  testAnswers: TestAnswersDto | undefined
  clientCategorizationPolicy: string | undefined

  handleFormSubmit: (values: ProfessionalClassificationTestValues) => void

  isFormEnabled: boolean

  classificationName: string | undefined
}

const ProfessionalClassificationTestFormUI: React.FC<
  FormikProps<ProfessionalClassificationTestValues> & OuterProps
> = (props) => {
  const { clientCategorizationPolicy, handlePortfolioModalOpen, handleTradeSizeModalOpen } = props
  const { testData, values, setFieldValue, setFieldTouched, handleSubmit, isFormEnabled } = props
  const { classificationName, errors, isValid, isSubmitting, setSubmitting, testAnswers } = props

  const { significantPortfolio, professionalExperience, significantTradeSize } = values
  const { selectedAnswers, freeAnswers } = values

  const { account } = useAccountReadContext()
  const { categorySizes } = useCategorySizesReadContext()
  const { refreshCategorySizes } = useCategorySizesWriteContext()

  useEffect(() => {
    refreshCategorySizes()
  }, [])

  const { t } = useTranslation()

  const getAffirmativeAnswers = () => {
    const hasProfessionalExperienceValue = professionalExperience === RadioCodeEnum.SELECTED ? 1 : 0
    const hasSignificantTradeSizeValue = significantTradeSize === RadioCodeEnum.SELECTED ? 1 : 0
    const hasSignificantPortfolioValue = significantPortfolio === RadioCodeEnum.SELECTED ? 1 : 0

    return (
      hasProfessionalExperienceValue + hasSignificantPortfolioValue + hasSignificantTradeSizeValue
    )
  }

  const canSubmit = (): boolean => getAffirmativeAnswers() > 1

  const handleTradeSizeProofOnChange = async (files: FileData[]) => {
    setFieldValue('tradeSizeProofs', [...files])
  }
  const handlePortfolioProofOnChange = async (files: FileData[]) => {
    setFieldValue('portfolioProofs', [...files])
  }

  const handleSubmitForm = async (event: React.FormEvent<HTMLFormElement>) => {
    setSubmitting(true)
    event.preventDefault()
    handleSubmit()
  }

  const getFieldName = (index: number) => {
    switch (index) {
      case 1:
        return 'employerName'
      case 2:
        return 'jobTitle'
      case 3:
        return 'jobDuration'
      default:
        return 'professionalExperienceDetail'
    }
  }
  const getConfirmationFieldName = (index: number) => {
    switch (index) {
      case 0:
        return 'classificationDeclaration1'
      case 1:
        return 'classificationDeclaration2'
      default:
        return 'classificationDeclaration3'
    }
  }

  const mapSectionIndex = (index: any): boolean => {
    switch (index) {
      case 0:
        return significantTradeSize === RadioCodeEnum.SELECTED
      case 1:
        return significantPortfolio === RadioCodeEnum.SELECTED
      default:
        return professionalExperience === RadioCodeEnum.SELECTED
    }
  }
  const mapQuestionName = (name: string): string => {
    switch (name) {
      case 'classification_question_1':
        return 'significantTradeSize'
      case 'classification_question_2':
        return 'significantPortfolio'
      default:
        return 'professionalExperience'
    }
  }
  return (
    <Form onSubmit={handleSubmitForm}>
      <Box bg='surface-white-smoke-0' radius='x16' p='x24' my='x8'>
        <Body1 isParagraph medium>
          <Body1 color='text-low-em'>{t('Profile.Current classification status:')}</Body1>{' '}
          <Body1 color='text-success-high-em'>{classificationName}</Body1>
        </Body1>
      </Box>
      <div className={styles.card}>
        <Heading6 medium>
          {t('Profile.Application Form to be Classified as a Professional Client')}
        </Heading6>
        <p />
        <Body1 isParagraph>
          {t('Profile.Please fill in the below form if you wish to apply for')}
        </Body1>
        <Body1 isParagraph>{t('Profile.You may request a different categorisation')}</Body1>
        <Body1 isParagraph>
          {t('Profile.Before you complete the Assessment Form, please read the')}{' '}
          {clientCategorizationPolicy ? (
            <Link href={clientCategorizationPolicy} size='lg'>
              {t('Profile.Client Categorisation Policy')}
            </Link>
          ) : (
            t('Profile.Client Categorisation Policy')
          )}{' '}
          {t(
            'Profile.to understand the rights, protections and loss of protections of being classified as a professional client.'
          )}
        </Body1>
        <Body1 isParagraph medium>
          {t('Profile.Confirmation of the consequences of changes to your FCA protection', {
            organization: isTickmillEU(account) ? 'CySEC' : 'FCA',
          })}
        </Body1>
        <Body1 isParagraph>{t('Profile.We have highlighted the protections that you')}</Body1>
      </div>
      <ClassificationText />
      <div className={classNames(styles.card, 'is-flex align-content-center')}>
        <Body1 isParagraph>
          {t(
            'Profile.To qualify as a Professional client, you need to meet 2 out of the 3 criteria stipulated below'
          )}
        </Body1>

        <div className={styles.overviewSection}>
          {testData.sections.map((section, sectionIndex) => {
            const selected = mapSectionIndex(sectionIndex)
            if (sectionIndex > 2) {
              return null
            }
            return (
              <div className={styles.cardWrapper} key={sectionIndex}>
                <OverviewCard
                  title={`${sectionIndex + 1}. ${section.name}`}
                  text={(!!section.questions.length && section.questions[0].question.name) || ''}
                  handelInfoOnClick={
                    isZero(sectionIndex) ? handleTradeSizeModalOpen : handlePortfolioModalOpen
                  }
                  hasIcon={sectionIndex <= 1}
                >
                  {section.questions.map((question, questionIndex) => {
                    const fieldName = mapQuestionName(question.widget.name || '')
                    const error = getIn(errors, fieldName)

                    if (question.widget.type === 'radio') {
                      return (
                        <React.Fragment key={questionIndex}>
                          <div className={classNames('is-flex mb-4')}>
                            {question.answers.map((answer) => (
                              <Radio
                                disabled={!isFormEnabled}
                                key={answer.id}
                                value={answer.code}
                                name={fieldName}
                                onChange={async () => {
                                  const filteredArray = selectedAnswers.filter(
                                    (x) => x.question !== question.question.id
                                  )
                                  const newValue = {
                                    question: question.question.id,
                                    answer: answer.id,
                                  }
                                  setFieldValue('selectedAnswers', [...filteredArray, newValue])
                                  setFieldValue(
                                    mapQuestionName(question.widget.name || ''),
                                    answer.code
                                  )
                                }}
                              >
                                {answer.name} *
                              </Radio>
                            ))}
                          </div>
                          {error !== t('Validation.Required') && (
                            <div className='help is-danger'>{error}</div>
                          )}

                          {isFormEnabled && sectionIndex < 2 && (
                            <div
                              className={classNames(
                                {
                                  'is-hidden': !selected,
                                },
                                'mt-4'
                              )}
                            >
                              {
                                <DocumentFileUpload
                                  acceptType={[DocumentFileExtensionType.PDF]}
                                  categorySizes={categorySizes}
                                  documentCategory={DocumentCategoryType.Additional}
                                  onUpload={
                                    isZero(sectionIndex)
                                      ? handleTradeSizeProofOnChange
                                      : handlePortfolioProofOnChange
                                  }
                                  multiple
                                />
                              }
                            </div>
                          )}
                        </React.Fragment>
                      )
                    } else if (isFormEnabled && question.widget.type === 'short_text' && selected) {
                      if (question.question.name.length > 30) {
                        return (
                          <Textarea
                            key={question.question.id}
                            label={question.question.name}
                            value={values[getFieldName(questionIndex)] || ''}
                            onChange={async (e: React.ChangeEvent<HTMLTextAreaElement>) => {
                              const { value } = e.target
                              const filteredArray = freeAnswers.filter(
                                (x) => x.testQuestionId !== question.question.id
                              )
                              const newValue = {
                                testQuestionId: question.question.id,
                                answer: value,
                              }

                              await setFieldValue('freeAnswers', [...filteredArray, newValue])
                              setFieldValue(getFieldName(questionIndex), value)
                            }}
                          />
                        )
                      }
                      return (
                        <div className={'mb-4'} key={questionIndex}>
                          <Textarea
                            key={question.question.id}
                            label={question.question.name}
                            value={values[getFieldName(questionIndex)] || ''}
                            onChange={async (e: React.ChangeEvent<HTMLTextAreaElement>) => {
                              const { value } = e.target
                              const filteredArray = freeAnswers.filter(
                                (x) => x.testQuestionId !== question.question.id
                              )
                              const newValue = {
                                testQuestionId: question.question.id,
                                answer: value,
                              }
                              await setFieldValue('freeAnswers', [...filteredArray, newValue])
                              setFieldValue(getFieldName(questionIndex), value)
                            }}
                          />
                        </div>
                      )
                    } else if (isFormEnabled && question.widget.type === 'text' && selected) {
                      return (
                        <React.Fragment key={questionIndex}>
                          <Body1 isParagraph>{question.question.name}</Body1>
                          <Textarea
                            label={question.question.name}
                            key={question.question.id}
                            name={values[getFieldName(questionIndex)] || ''}
                            onChange={async (e: React.ChangeEvent<HTMLTextAreaElement>) => {
                              const { value } = e.target
                              const filteredArray = freeAnswers.filter(
                                (x) => x.testQuestionId !== question.question.id
                              )
                              const newValue = {
                                testQuestionId: question.question.id,
                                answer: value,
                              }
                              await setFieldValue('freeAnswers', [...filteredArray, newValue])
                              setFieldValue(getFieldName(questionIndex), value)
                            }}
                          />
                        </React.Fragment>
                      )
                    }
                    return null
                  })}

                  {!isFormEnabled &&
                    professionalExperience === RadioCodeEnum.SELECTED &&
                    testAnswers &&
                    testAnswers?.sections.length > 2 &&
                    testAnswers?.sections[sectionIndex].answers.map((answer) => {
                      if (
                        answer.question.widgetType === 'short_text' ||
                        (answer.question.widgetType === 'text' && answer.answer.name)
                      ) {
                        return (
                          <div key={answer.question.id}>
                            <Caption1 color='text-low-em' isParagraph>
                              {answer.question.name}
                            </Caption1>
                            <Body1 isParagraph medium>
                              {answer.answer.name}
                            </Body1>
                          </div>
                        )
                      }
                      return null
                    })}
                </OverviewCard>
              </div>
            )
          })}
        </div>
        <div className='my-2 pl-4'>
          {!isFormEnabled && (
            <Body1 isParagraph>
              {t('Profile.Uploaded documents')}{' '}
              {testAnswers?.documents.map((doc) => doc.name).join(', ') || null}
            </Body1>
          )}
        </div>
      </div>
      <div className='mt-5 pl-4'>
        {testData.sections.map((section, sectionIndex) => {
          if (sectionIndex === 3) {
            return section.questions.map((question, questionIndex) => {
              const fieldName = getConfirmationFieldName(questionIndex)
              const error = getIn(errors, fieldName)
              return (
                <div className={styles.confirm} key={question.question.id}>
                  <Checkbox
                    checked={getIn(values, fieldName)}
                    name={fieldName}
                    disabled={!isFormEnabled}
                    error={error}
                    onChange={async (checked: boolean) => {
                      const filteredArray = freeAnswers.filter(
                        (x) => x.testQuestionId !== question.question.id
                      )
                      const newValue = {
                        testQuestionId: question.question.id,
                        answer: checked ? '1' : '0',
                      }
                      setFieldValue('freeAnswers', [...filteredArray, newValue])
                      await setFieldValue(fieldName, checked)
                      setFieldTouched(fieldName)
                    }}
                  >
                    {question.question.name}
                  </Checkbox>
                </div>
              )
            })
          }
          return null
        })}
        <div className={classNames(styles.buttonWrapper, 'is-flex is-justify-content-center mt-6')}>
          {isFormEnabled && (
            <Button.Primary
              size='lg'
              type='submit'
              disabled={!canSubmit() || !isValid || isSubmitting}
            >
              {t('Confirm')}
            </Button.Primary>
          )}
        </div>
      </div>
    </Form>
  )
}

const findDuplicateFile = (proofs: FileData[], otherProofs: FileData[]) =>
  proofs.filter((firstItem: FileData) => {
    return [...proofs, ...otherProofs].some((secondItem: FileData) => {
      return (
        firstItem.id !== secondItem.id &&
        firstItem?.fileName &&
        secondItem?.fileName &&
        firstItem.fileName === secondItem.fileName &&
        firstItem.fileSize === secondItem.fileSize
      )
    })
  })

export const ProfessionalClassificationTestForm = withFormik<
  OuterProps,
  ProfessionalClassificationTestValues
>({
  mapPropsToValues: ({ testAnswers }) => {
    const answers: TestAnswerDto[] =
      testAnswers?.sections.map((section) => section.answers).flat() || []

    return {
      freeAnswers: [],
      selectedAnswers: [],
      significantTradeSize:
        (!testAnswers?.allowRetake &&
          answers.find((a) => a.question.widgetName === 'classification_question_1')?.answer
            .code) ||
        '',
      significantPortfolio:
        (!testAnswers?.allowRetake &&
          answers.find((a) => a.question.widgetName === 'classification_question_2')?.answer
            .code) ||
        '',
      professionalExperience:
        (!testAnswers?.allowRetake &&
          answers.find((a) => a.question.widgetName === 'classification_question_3')?.answer
            .code) ||
        '',
      portfolioProofs: [],
      tradeSizeProofs: [],
      employerName: '',
      jobTitle: '',
      jobDuration: '',
      professionalExperienceDetail: '',
      classificationDeclaration1:
        !testAnswers?.allowRetake &&
        answers.find((a) => a.question.widgetName === 'classification_declaration_1')?.answer
          .name === RadioCodeEnum.SELECTED,
      classificationDeclaration2:
        !testAnswers?.allowRetake &&
        answers.find((a) => a.question.widgetName === 'classification_declaration_2')?.answer
          .name === RadioCodeEnum.SELECTED,
      classificationDeclaration3:
        !testAnswers?.allowRetake &&
        answers.find((a) => a.question.widgetName === 'classification_declaration_3')?.answer
          .name === RadioCodeEnum.SELECTED,
    }
  },
  validate: (values) => {
    const errors: FormikErrors<ProfessionalClassificationTestValues> = {}

    const tradeSizeProofDuplicates = findDuplicateFile(
      values?.tradeSizeProofs,
      values?.portfolioProofs
    )
    const portfolioProofsDuplicates = findDuplicateFile(
      values?.portfolioProofs,
      values?.tradeSizeProofs
    )

    if (tradeSizeProofDuplicates?.length) {
      errors.significantTradeSize = t('Validation.Error: Document is already uploaded')
    }

    if (portfolioProofsDuplicates?.length) {
      errors.significantPortfolio = t('Validation.Error: Document is already uploaded')
    }

    if (!values.classificationDeclaration1) {
      errors.classificationDeclaration1 = t('Validation.Required')
    }
    if (!values.classificationDeclaration2) {
      errors.classificationDeclaration2 = t('Validation.Required')
    }
    if (!values.classificationDeclaration3) {
      errors.classificationDeclaration3 = t('Validation.Required')
    }
    if (values.significantPortfolio === '') {
      errors.significantPortfolio = t('Validation.Required')
    }
    if (values.significantTradeSize === '') {
      errors.significantTradeSize = t('Validation.Required')
    }
    if (values.professionalExperience === '') {
      errors.professionalExperience = t('Validation.Required')
    }
    if (values.professionalExperience === RadioCodeEnum.SELECTED && !values.employerName) {
      errors.employerName = t('Validation.Employer name is required')
    }
    if (values.professionalExperience === RadioCodeEnum.SELECTED && !values.jobTitle) {
      errors.jobTitle = t('Validation.Job title is required')
    }
    if (values.professionalExperience === RadioCodeEnum.SELECTED && !values.jobDuration) {
      errors.jobDuration = t('Validation.Working period is required')
    }
    if (
      values.significantPortfolio === RadioCodeEnum.SELECTED &&
      (values.portfolioProofs.filter((f) => f.error).length ||
        !values.portfolioProofs.filter((f) => f.fileName).length)
    ) {
      errors.portfolioProofs = t('Validation.Portfolio proof must be uploaded')
    }
    if (
      values.significantTradeSize === RadioCodeEnum.SELECTED &&
      (values.tradeSizeProofs.filter((f) => f.error).length ||
        !values.tradeSizeProofs.filter((f) => f.fileName).length)
    ) {
      errors.tradeSizeProofs = t('Validation.Trade proof must be uploaded')
    }
    return errors
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.handleFormSubmit(values)
    } catch {
      setSubmitting(false)
    }
  },
  enableReinitialize: true,
  isInitialValid: false,
})(ProfessionalClassificationTestFormUI)
